import React, { useState, useEffect } from 'react';

const PLACEHOLDER =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjhmOGY4Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkNhcmdhbmRvLi4uPC90ZXh0Pjwvc3ZnPg==';

const OptimizedImage = ({
  src,
  alt,
  className = '',
  placeholder = PLACEHOLDER,
  eager = false,
  ...props
}) => {
  const [imageSrc, setImageSrc] = useState(eager && src ? src : placeholder);
  const [imageRef, setImageRef] = useState();
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasStarted, setHasStarted] = useState(Boolean(eager && src));

  // Lazy-load: cargar la primera vez que entra en viewport
  useEffect(() => {
    if (eager || hasStarted || !imageRef || !src) return undefined;

    let observer;
    let didCancel = false;

    if (typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (
              !didCancel &&
              (entry.intersectionRatio > 0 || entry.isIntersecting)
            ) {
              setHasStarted(true);
              setImageSrc(src);
              setIsLoaded(false);
              observer.unobserve(imageRef);
            }
          });
        },
        {
          threshold: 0.01,
          rootMargin: '75%',
        }
      );
      observer.observe(imageRef);
    } else {
      setHasStarted(true);
      setImageSrc(src);
      setIsLoaded(false);
    }

    return () => {
      didCancel = true;
      if (observer && imageRef) {
        observer.unobserve(imageRef);
      }
    };
  }, [eager, hasStarted, imageRef, src]);

  // Cuando cambia src (p. ej. galería), actualizar la vista previa
  useEffect(() => {
    if (!src) {
      setImageSrc(placeholder);
      setIsLoaded(false);
      return;
    }
    if (eager || hasStarted) {
      setImageSrc(src);
      setIsLoaded(false);
    }
  }, [src, eager, hasStarted, placeholder]);

  return (
    <img
      {...props}
      ref={setImageRef}
      src={imageSrc || placeholder}
      alt={alt}
      className={`${className} ${isLoaded ? 'image-loaded' : ''}`}
      onLoad={() => {
        setIsLoaded(true);
      }}
      loading={eager ? 'eager' : 'lazy'}
      style={{
        ...(props.style || {}),
        transition: 'opacity 0.3s ease-in-out, transform 0.6s var(--brand-ease, ease)',
        opacity: isLoaded ? 1 : 0.7,
      }}
    />
  );
};

export default OptimizedImage;
